import { Ranges } from "./ranges.js";

/**
 * Case-insensitive fuzzy subsequence match. Returns inclusive [from, to]
 * ranges per the `Ranges` convention.
 *
 * Picks the match that maximises contiguous runs (fewest gaps); ties are
 * broken in favour of the earliest end position.
 *
 * Returns an empty ranges array if `query` is not a subsequence of `text`.
 *
 * DP: O(n*m) time and space, where n = text.length and m = query.length.
 *   dp[j][i]   = minimum number of gaps to match query[0..=j] with query[j]
 *                placed at text[i] (only defined where text[i] === query[j]).
 *   pred[j][i] = chosen predecessor position used to reach dp[j][i].
 * For each row j we keep a running prefix-min over dp[j-1][0..i-2] so each
 * cell is O(1) — option A (continue a run, pred = i-1) or option B (start
 * a new run from any earlier predecessor, pays +1 gap).
 */
export function fuzzySearch(text: string, query: string): Ranges.Ranges<number> {
  if (!query.length || query.length > text.length)
    return [] as number[] as Ranges.Ranges<number>;

  const t = text.toLowerCase();
  const q = query.toLowerCase();
  const n = t.length;
  const m = q.length;
  const INF = Infinity;

  const dp: number[][] = [];
  const pred: number[][] = [];
  for (let j = 0; j < m; j++) {
    dp.push(new Array(n).fill(INF));
    pred.push(new Array(n).fill(-1));
  }

  for (let i = 0; i < n; i++) {
    if (t[i] === q[0])
      dp[0][i] = 0;
  }

  for (let j = 1; j < m; j++) {
    // Running prefix-min over dp[j-1][0..i-2] as i grows. Keeps the smallest
    // argmin (updates strictly with <, so the first index reaching a new
    // min wins).
    let bestCost = INF;
    let bestIdx = -1;
    for (let i = j; i < n; i++) {
      // Fold dp[j-1][i-2] into the prefix-min (i' strictly less than i-1).
      if (i >= j + 1) {
        const idx = i - 2;
        if (dp[j-1][idx] < bestCost) {
          bestCost = dp[j-1][idx];
          bestIdx = idx;
        }
      }

      if (t[i] !== q[j])
        continue;

      let cellCost = INF;
      let cellPred = -1;
      // Option A: continue a run from i-1 (no gap added).
      if (dp[j-1][i-1] !== INF) {
        cellCost = dp[j-1][i-1];
        cellPred = i - 1;
      }
      // Option B: jump from an earlier predecessor (gap +1). Tie-break by
      // smaller predecessor index to bias the path leftward.
      if (bestCost !== INF) {
        const costB = bestCost + 1;
        if (costB < cellCost || (costB === cellCost && bestIdx < cellPred)) {
          cellCost = costB;
          cellPred = bestIdx;
        }
      }
      dp[j][i] = cellCost;
      pred[j][i] = cellPred;
    }
  }

  // Pick the best end position: min gaps, then earliest end.
  let bestEndCost = INF;
  let bestEnd = -1;
  for (let i = m - 1; i < n; i++) {
    if (dp[m-1][i] < bestEndCost) {
      bestEndCost = dp[m-1][i];
      bestEnd = i;
    }
  }
  if (bestEnd === -1)
    return [] as number[] as Ranges.Ranges<number>;

  // Reconstruct the chosen text positions.
  const positions = new Array<number>(m);
  let cur = bestEnd;
  for (let j = m - 1; j >= 0; j--) {
    positions[j] = cur;
    cur = pred[j][cur];
  }

  // Coalesce consecutive positions into inclusive [start, end] intervals.
  const ranges: number[] = [];
  let runStart = positions[0];
  let runEnd = positions[0];
  for (let k = 1; k < m; k++) {
    if (positions[k] === runEnd + 1) {
      runEnd = positions[k];
    } else {
      ranges.push(runStart, runEnd);
      runStart = positions[k];
      runEnd = positions[k];
    }
  }
  ranges.push(runStart, runEnd);
  return ranges as Ranges.Ranges<number>;
}